import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../components/ui/PageHeader'
import { Spinner } from '../components/ui/Spinner'
import { Alert } from '../components/ui/Alert'
import {
  getSyncQueue,
  removeFromSyncQueue,
  clearSyncQueue,
  type SyncQueueItem,
} from '../lib/offlineDb'
import { useOfflineSync } from '../hooks/useOfflineSync'

const ACTION_LABELS: Record<string, string> = {
  create: 'Criar',
  update: 'Atualizar',
  delete: 'Excluir',
}

const TABLE_LABELS: Record<string, string> = {
  resultados: 'Resultado',
  atiradores: 'Atirador',
  inscricoes: 'Inscricao',
}

export function OfflineQueuePage() {
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const { isOnline, syncing, syncPending } = useOfflineSync()

  const { data: queue, isLoading, refetch } = useQuery<SyncQueueItem[]>({
    queryKey: ['offline-queue'],
    queryFn: getSyncQueue,
  })

  async function handleRemove(id: number) {
    await removeFromSyncQueue(id)
    await refetch()
    setAlert({ type: 'success', message: 'Item removido da fila' })
  }

  async function handleClear() {
    await clearSyncQueue()
    await refetch()
    setAlert({ type: 'success', message: 'Fila limpa' })
  }

  async function handleSync() {
    const result = await syncPending()
    await refetch()
    if (result.success > 0) {
      setAlert({ type: 'success', message: `${result.success} item(s) sincronizado(s)!` })
    }
    if (result.failed > 0) {
      setAlert({ type: 'error', message: `${result.failed} falha(s): ${result.errors.join('; ')}` })
    }
  }

  function formatPayload(payload: Record<string, unknown>): string {
    try {
      return Object.entries(payload)
        .slice(0, 4)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ')
    } catch {
      return '—'
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Fila Offline"
        description="Operacoes pendentes para sincronizar com o servidor"
      />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Status bar */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`text-2xl ${isOnline ? 'text-green-500' : 'text-red-500'}`}>
            {isOnline ? '📶' : '📴'}
          </span>
          <div>
            <p className="font-semibold text-compumaq-texto">
              {isOnline ? 'Conectado' : 'Modo Offline'}
            </p>
            <p className="text-sm text-compumaq-textoSuave">
              {(queue?.length ?? 0)} operacao(oes) pendente(s)
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {isOnline && (
            <button
              onClick={handleSync}
              disabled={syncing || !queue?.length}
              className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm font-semibold rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50 transition-colors"
            >
              {syncing ? 'Sincronizando...' : 'Sincronizar Tudo'}
            </button>
          )}
          {queue && queue.length > 0 && (
            <button
              onClick={handleClear}
              className="px-4 py-2 border border-red-300 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors"
            >
              Limpar Fila
            </button>
          )}
        </div>
      </div>

      {/* Lista */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !queue?.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">✅</p>
            <p className="font-medium">Fila vazia</p>
            <p className="text-sm text-compumaq-textoSuave mt-1">Nenhuma operacao pendente para sincronizar.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">#</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Acao</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Tabela</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Payload</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Metodo</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Tentativas</th>
                  <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Criado</th>
                  <th className="text-right px-4 py-3 font-medium text-compumaq-textoSuave">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-compumaq-dourado/25">
                {queue.map((item: SyncQueueItem, idx: number) => (
                  <tr key={item.id} className="hover:bg-compumaq-preto">
                    <td className="px-4 py-3 text-compumaq-textoSuave font-mono text-xs">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        item.action === 'create' ? 'bg-green-100 text-green-700' :
                        item.action === 'update' ? 'bg-compumaq-dourado/15 text-compumaq-doradoClaro' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {ACTION_LABELS[item.action] ?? item.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-compumaq-texto">{TABLE_LABELS[item.table] ?? item.table}</td>
                    <td className="px-4 py-3 text-compumaq-textoSuave text-xs max-w-[200px] truncate" title={JSON.stringify(item.payload)}>
                      {formatPayload(item.payload)}
                    </td>
                    <td className="px-4 py-3 text-compumaq-textoSuave text-xs font-mono">{item.method}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-mono ${item.retryCount > 0 ? 'text-orange-600' : 'text-compumaq-textoSuave'}`}>
                        {item.retryCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-compumaq-textoSuave text-xs">
                      {item.createdAt ? new Date(item.createdAt).toLocaleString('pt-BR') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleRemove(item.id!)}
                        className="text-xs text-red-600 hover:text-red-800 font-medium"
                      >
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
